import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, withQuery, money, date, unitPrice, quantity, PAYMENT_LABEL } from '../api.js';
import Lookup from '../components/Lookup.jsx';
import { Pager } from './Products.jsx';

/**
 * Фактуриране — обединяване на продажби по протокол в една фактура.
 *
 * Част от клиентите се обслужват през месеца с отделни продажби, всяка
 * с приемо-предавателен протокол вместо касова бележка (плащат по банков
 * път, затова фискалното мълчи — виж бележката в services/documents.js).
 * Накрая на месеца всичко се събира в ЕДНА фактура — не нов документ,
 * а един и същ номер, записан на всички избрани продажби наведнъж
 * (`POST /api/invoices`, виж routes/invoices.js).
 *
 * Табовете живеят в адреса (?view=list), не в локално състояние — виж
 * [[dokumenti-vgraden-v-prodazhba-dostavka]] защо: иначе „Назад" след
 * отваряне на стара фактура се връща на празната форма, не на списъка.
 */
export default function Invoicing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const view = searchParams.get('view') === 'list' ? 'list' : 'new';

  return (
    <>
      <div className="page-head"><h1>Фактуриране</h1></div>

      <div className="tabs">
        <button className={`tab ${view === 'new' ? 'active' : ''}`}
          onClick={() => { if (view !== 'new') navigate('/invoicing'); }}>
          Нова фактура
        </button>
        <button className={`tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => navigate('/invoicing?view=list')}>
          Издадени фактури
        </button>
      </div>

      {view === 'list' ? <IssuedInvoices /> : <NewInvoice />}
    </>
  );
}

function NewInvoice() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Избраният клиент живее в адреса (?partner=…), не в локално състояние
  // — иначе „Назад" след отваряне на фирма (документ, печат) се връща на
  // празната форма, не на списъка с фирмите. Виж същата поправка при
  // „Стари продажби"/„Стари прехвърляния" — [[dokumenti-vgraden-v-prodazhba-dostavka]].
  const partnerId = searchParams.get('partner') ? Number(searchParams.get('partner')) : null;
  const partnerName = searchParams.get('partnerName');

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(true);

  function loadPending() {
    setPendingLoading(true);
    api.get('/api/invoices/pending-partners')
      .then((r) => setPending(r.items))
      .catch(() => setPending([]))
      .finally(() => setPendingLoading(false));
  }

  useEffect(() => { loadPending(); }, []);

  useEffect(() => {
    setError(null);
    setDone(null);
    if (!partnerId) { setItems([]); setSelected(new Set()); return; }
    setLoading(true);
    api.get(`/api/partners/${partnerId}/pending-invoice-sales`)
      .then((r) => {
        setItems(r.items);
        setSelected(new Set(r.items.map((it) => it.id)));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  function pickPartner(id, name) {
    if (!id) { navigate('/invoicing'); return; }
    navigate(`/invoicing?partner=${id}&partnerName=${encodeURIComponent(name)}`);
  }

  function toggle(id) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((it) => it.id))));
  }

  const chosen = items.filter((it) => selected.has(it.id));
  const chosenTotal = chosen.reduce((s, it) => s + Number(it.total), 0);
  const currency = items[0]?.currency;

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/invoices', {
        partnerId,
        documentIds: [...selected],
      });
      setDone(res);
      setItems((its) => its.filter((it) => !selected.has(it.id)));
      setSelected(new Set());
      loadPending();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <label className="field" style={{ maxWidth: 420 }}>
          Клиент
          <Lookup
            url="/api/partners"
            value={partnerId}
            label={partnerName}
            placeholder="търсене по име или ЕИК"
            onPick={pickPartner}
          />
        </label>
        {partnerId && (
          <p className="hint" style={{ marginTop: 6, marginBottom: 0 }}>
            Продажби с плащане по банков път, записани, без взет номер на фактура —
            обичайно въведени с приемо-предавателен протокол през месеца.
          </p>
        )}
      </div>

      {error && <div className="error" style={{ margin: '0 16px 16px' }}>{error}</div>}
      {done && (
        <div className="notice" style={{ margin: '0 16px 16px' }}>
          Издадена фактура № {done.invoiceNo} от {date(done.invoiceDate)} за {done.documentCount}{' '}
          {done.documentCount === 1 ? 'документ' : 'документа'}.{' '}
          <a href={`/print/invoice/${done.invoiceNo}`} target="_blank" rel="noreferrer">Отвори за печат</a>
        </div>
      )}

      {!partnerId && (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <div className="card-head" style={{ padding: '8px 0' }}>
            Фирми с чакащи протоколи
          </div>
          {pendingLoading ? <div className="empty">Зареждане…</div> : pending.length === 0 ? (
            <p className="empty" style={{ margin: 0 }}>
              Няма фирма с чакащи фактуриране продажби.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="compact">
                <thead>
                  <tr>
                    <th>Контрагент</th>
                    <th>Най-стар протокол</th>
                    <th className="num">Документи</th>
                    <th className="num">Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((p) => (
                    <tr key={p.partnerId} style={{ cursor: 'pointer' }}
                      onClick={() => pickPartner(p.partnerId, p.partnerName)}>
                      <td><a href="#" onClick={(e) => e.preventDefault()}>{p.partnerName}</a></td>
                      <td className="muted">{date(p.oldestDocDate)}</td>
                      <td className="num">{p.documentCount}</td>
                      <td className="num">{money(p.total, p.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {partnerId && (
        loading ? <div className="empty">Зареждане…</div> : items.length === 0 ? (
          <div className="card-body">
            <p className="empty" style={{ margin: 0 }}>
              Няма чакащи фактуриране продажби за този клиент.
            </p>
          </div>
        ) : (
          <>
            <ProtokoliSRedove key={`${partnerId}-${done?.invoiceNo ?? ''}`} items={items}
              selected={selected} onToggle={toggle} onToggleAll={toggleAll} />
            <div className="card-body" style={{
              display: 'flex', justifyContent: 'flex-end', gap: 12,
              alignItems: 'center', flexWrap: 'wrap',
            }}>
              <span className="hint">
                избрани {chosen.length} от {items.length} · общо {money(chosenTotal, currency)}
              </span>
              <button className="primary" disabled={chosen.length === 0 || busy} onClick={issue}>
                {busy ? 'Издаване…' : 'Издай фактура'}
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
}

/*
 * Двата прозореца (30.10.2026, Светлана): горе протоколите, долу редовете
 * на маркирания. ↑/↓ местят маркера, PgUp/PgDn по 10, Home/End; с
 * `selected` има и отметки — интервалът слага/маха. Ползва се в „Нова
 * фактура“ и при отворена издадена фактура. Редовете се четат при първото
 * маркиране и се помнят, за да не се чака при връщане със стрелките.
 *
 * items — { id, doc_number, doc_date, subtotal, vat_total, total, currency, is_test }
 */
function ProtokoliSRedove({ items, selected, onToggle, onToggleAll, onEsc }) {
  const [cursor, setCursor] = useState(0);
  const [redove, setRedove] = useState({});
  const listRef = useRef(null);
  const sOtmetki = !!selected;

  // Стрелките работят веднага, без първо да се кликва в списъка.
  useEffect(() => { listRef.current?.focus(); }, []);

  const marked = items[Math.min(cursor, items.length - 1)] ?? null;
  useEffect(() => {
    if (!marked || redove[marked.id]) return;
    api.get(`/api/documents/${marked.id}`)
      .then((d) => setRedove((m) => ({ ...m, [marked.id]: d })))
      .catch((e) => setRedove((m) => ({ ...m, [marked.id]: { error: e.message } })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marked?.id]);

  useEffect(() => {
    listRef.current?.querySelector(`tr[data-i="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  function onListKey(e) {
    if (!items.length) return;
    const k = e.key;
    if (k === 'ArrowDown') setCursor((c) => Math.min(c + 1, items.length - 1));
    else if (k === 'ArrowUp') setCursor((c) => Math.max(c - 1, 0));
    else if (k === 'PageDown') setCursor((c) => Math.min(c + 10, items.length - 1));
    else if (k === 'PageUp') setCursor((c) => Math.max(c - 10, 0));
    else if (k === 'Home') setCursor(0);
    else if (k === 'End') setCursor(items.length - 1);
    else if (k === ' ' && marked && sOtmetki) onToggle(marked.id);
    else if (k === 'Escape' && onEsc) onEsc();
    else return;
    e.preventDefault();
  }

  return (
    <>
      <div className="table-wrap" ref={listRef} tabIndex={0} onKeyDown={onListKey}
        style={{ maxHeight: '38vh', outline: 'none' }}
        title={sOtmetki ? '↑/↓ — маркер, интервал — отметка' : '↑/↓ — маркер'}>
        <table className="compact">
          <thead>
            <tr>
              {sOtmetki && (
                <th style={{ width: 30 }}>
                  <input type="checkbox"
                    checked={selected.size === items.length && items.length > 0}
                    onChange={onToggleAll} />
                </th>
              )}
              <th>Документ</th>
              <th>Дата</th>
              <th className="num">Основа</th>
              <th className="num">ДДС</th>
              <th className="num">Сума</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id} data-i={i} style={{
                cursor: 'pointer',
                ...(marked?.id === it.id ? { background: 'var(--accent-soft)', boxShadow: 'inset 3px 0 0 var(--accent)' } : {}),
              }}
                onClick={() => { setCursor(i); listRef.current?.focus(); }}>
                {sOtmetki && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(it.id)} tabIndex={-1}
                      onChange={() => { onToggle(it.id); setCursor(i); listRef.current?.focus(); }} />
                  </td>
                )}
                <td>
                  <Link to={`/documents/${it.id}`} tabIndex={-1} onClick={(e) => e.stopPropagation()}>{it.doc_number}</Link>
                  {!!Number(it.is_test) && <span className="badge" style={{ marginLeft: 6 }}>проба</span>}
                </td>
                <td className="muted">{date(it.doc_date)}</td>
                <td className="num">{money(it.subtotal, it.currency)}</td>
                <td className="num">{money(it.vat_total, it.currency)}</td>
                <td className="num">{money(it.total, it.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {marked && <ProtokolRedove doc={marked} data={redove[marked.id]} />}
    </>
  );
}

/** Долният прозорец — какво е изписано на маркирания протокол. */
function ProtokolRedove({ doc, data }) {
  const zaglavie = (
    <div className="card-head" style={{ padding: '8px 16px', borderTop: '1px solid var(--border)' }}>
      Протокол № {doc.doc_number} от {date(doc.doc_date)}
      <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
        {money(doc.total, doc.currency)} с ДДС
      </span>
    </div>
  );
  if (!data) return <>{zaglavie}<div className="empty">Зареждане…</div></>;
  if (data.error) return <>{zaglavie}<div className="error" style={{ margin: '0 16px' }}>{data.error}</div></>;
  return (
    <>
      {zaglavie}
      <div className="table-wrap" style={{ maxHeight: '32vh' }}>
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 40 }}>№</th>
              <th>Наименование</th>
              <th className="num">Количество</th>
              <th className="num">Ед. цена без ДДС</th>
              <th className="num">Сума без ДДС</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => {
              const anuliran = Number(it.is_cancelled) === 1;
              const style = anuliran ? { textDecoration: 'line-through' } : undefined;
              return (
                <tr key={it.id} className={anuliran ? 'muted' : undefined}>
                  <td className="muted">{it.line_no}</td>
                  <td style={style}>
                    {it.description}
                    {Number(it.is_gift) === 1 && <span className="badge muted" style={{ marginLeft: 6 }}>подарък</span>}
                    {it.note && <span className="muted"> — {it.note}</span>}
                  </td>
                  <td className="num" style={style}>{quantity(it.quantity)} {it.unit}</td>
                  <td className="num" style={style}>{Number(it.is_gift) === 1 ? '—' : unitPrice(it, it.currency)}</td>
                  <td className="num" style={style}>{money(it.line_subtotal, it.currency)}</td>
                </tr>
              );
            })}
            {data.items.length === 0 && <tr><td colSpan={5} className="empty">Няма редове</td></tr>}
          </tbody>
        </table>
      </div>
      {data.note && <div className="hint" style={{ padding: '4px 16px 0' }}>Бележка: {data.note}</div>}
    </>
  );
}

function IssuedInvoices() {
  const PAGE = 50;
  /*
   * Отворена фактура (30.10.2026, Светлана) — в адреса (?inv=…), за да
   * работи и „Назад“ на браузъра; списъкът остава жив отдолу, с филтрите,
   * страницата и маркера, и при връщане е където е бил.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const otvorena = searchParams.get('inv');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef(null);
  function otvori(no) {
    setSearchParams((cur) => { const n = new URLSearchParams(cur); n.set('inv', no); return n; });
  }
  function zatvori() {
    setSearchParams((cur) => { const n = new URLSearchParams(cur); n.delete('inv'); return n; });
  }
  const [partnerQ, setPartnerQ] = useState('');
  const [q, setQ] = useState('');
  // По подразбиране само фактурите по няколко продажби (13.10.2026).
  const [multiOnly, setMultiOnly] = useState(true);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setOffset(0); setCursor(0); }, [partnerQ, q, multiOnly]);

  // След затваряне на фактурата стрелките продължават от същия ред.
  useEffect(() => { if (!otvorena) listRef.current?.focus(); }, [otvorena]);
  useEffect(() => {
    listRef.current?.querySelector(`tr[data-i="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor, data.items]);

  /*
   * ↑/↓ по фактурите, на края на страницата — към следващата/предишната;
   * PgUp/PgDn по 10, Home/End, Enter отваря. ↓ от полетата слиза тук.
   */
  const tek = Math.min(cursor, data.items.length - 1);
  function onListKey(e) {
    const n = data.items.length;
    if (!n) return;
    const k = e.key;
    if (k === 'ArrowDown') {
      if (tek < n - 1) setCursor(tek + 1);
      else if (offset + PAGE < data.total) { setOffset(offset + PAGE); setCursor(0); }
    } else if (k === 'ArrowUp') {
      if (tek > 0) setCursor(tek - 1);
      else if (offset > 0) { setOffset(Math.max(0, offset - PAGE)); setCursor(PAGE - 1); }
    } else if (k === 'PageDown') setCursor(Math.min(tek + 10, n - 1));
    else if (k === 'PageUp') setCursor(Math.max(tek - 10, 0));
    else if (k === 'Home') setCursor(0);
    else if (k === 'End') setCursor(n - 1);
    else if (k === 'Enter' && data.items[tek]) otvori(data.items[tek].invoiceNo);
    else return;
    e.preventDefault();
  }
  const kamSpisaka = (e) => { if (e.key === 'ArrowDown') { e.preventDefault(); listRef.current?.focus(); } };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      api.get(withQuery('/api/invoices', { partnerQ, q, multiOnly: multiOnly ? 1 : '', limit: PAGE, offset }))
        .then(setData)
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }, (partnerQ || q) ? 250 : 0);
    return () => clearTimeout(timer);
  }, [partnerQ, q, multiOnly, offset]);

  if (otvorena) return <IssuedInvoice invoiceNo={otvorena} onBack={zatvori} />;

  return (
    <>
      {error && <div className="error">{error}</div>}
      <div className="toolbar">
        <label className="field">
          Контрагент
          <input type="search" autoComplete="off" placeholder="име или ЕИК"
            value={partnerQ} onChange={(e) => setPartnerQ(e.target.value)} onKeyDown={kamSpisaka} />
        </label>
        <label className="field">
          Номер на фактура
          <input type="search" autoComplete="off" placeholder="напр. 0000050900"
            value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={kamSpisaka} />
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 7 }}>
          <input type="checkbox" checked={multiOnly}
            onChange={(e) => setMultiOnly(e.target.checked)} />
          само от няколко продажби
        </label>
      </div>
      <div className="card">
        <div className="table-wrap" ref={listRef} tabIndex={0} onKeyDown={onListKey}
          style={{ outline: 'none' }} title="↑/↓ — маркер, Enter — отвори">
          <table className="compact">
            <thead>
              <tr>
                <th>Фактура №</th>
                <th>Дата</th>
                <th>Контрагент</th>
                <th>Плащане</th>
                <th className="num">Документи</th>
                <th className="num">Сума</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((inv, i) => (
                <tr key={inv.invoiceNo} data-i={i} style={{
                  cursor: 'pointer',
                  ...(i === tek ? { background: 'var(--accent-soft)', boxShadow: 'inset 3px 0 0 var(--accent)' } : {}),
                }}
                  onClick={() => { setCursor(i); listRef.current?.focus(); }}
                  onDoubleClick={() => otvori(inv.invoiceNo)}>
                  <td>
                    <a href="#" tabIndex={-1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCursor(i); otvori(inv.invoiceNo); }}>
                      {inv.invoiceNo}
                    </a>
                  </td>
                  <td className="muted">{date(inv.invoiceDate)}</td>
                  <td>
                    {inv.partnerName
                      ? <Link to={`/partners/${inv.partnerId}`} tabIndex={-1} onClick={(e) => e.stopPropagation()}>{inv.partnerName}</Link>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="muted">
                    {String(inv.paymentMethods ?? '').split(',').filter(Boolean)
                      .map((m) => PAYMENT_LABEL[m] ?? m).join(', ') || '—'}
                  </td>
                  <td className="num">{inv.documentCount}</td>
                  <td className="num">{money(inv.total, inv.currency)}</td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="empty">Няма издадени фактури</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager total={data.total} offset={offset} setOffset={setOffset} loading={loading} page={PAGE} />
      </div>
    </>
  );
}

/*
 * Издадена фактура отворена (30.10.2026, Светлана) — като „Нова фактура“
 * с всички продажби отметнати: горе продажбите, долу редовете на
 * маркираната. „Печат“ отваря самата фактура — там са Печат, Мейл и PDF.
 * Esc или „← Към списъка“ връщат на издадените.
 */
function IssuedInvoice({ invoiceNo, onBack }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setInv(null);
    setError(null);
    api.get(`/api/invoices/${encodeURIComponent(invoiceNo)}`).then(setInv).catch((e) => setError(e.message));
  }, [invoiceNo]);

  const pechat = () => window.open(`/print/invoice/${encodeURIComponent(invoiceNo)}`, '_blank');
  const items = (inv?.documents ?? []).map((d) => ({
    id: d.id, doc_number: d.docNumber, doc_date: d.docDate,
    subtotal: d.subtotal, vat_total: d.vatTotal, total: d.total, currency: inv.currency,
  }));

  return (
    <div className="card">
      <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={onBack}>← Към списъка</button>
        <div>
          <strong>Фактура № {invoiceNo}</strong>
          {inv && (
            <span className="muted" style={{ marginLeft: 8 }}>
              от {date(inv.invoiceDate)} · {inv.partnerName ?? '—'} · {inv.documents.length}{' '}
              {inv.documents.length === 1 ? 'продажба' : 'продажби'}
            </span>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <button className="primary" onClick={pechat} title="Фактурата — с Печат, Мейл и PDF">Печат</button>
      </div>
      {error && <div className="error" style={{ margin: '0 16px 16px' }}>{error}</div>}
      {!inv && !error && <div className="empty">Зареждане…</div>}
      {inv && <ProtokoliSRedove items={items} onEsc={onBack} />}
      {inv && (
        <div className="card-body" style={{
          display: 'flex', justifyContent: 'flex-end', gap: 16, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <span className="hint">
            основа {money(inv.subtotal, inv.currency)} · ДДС {money(inv.vatTotal, inv.currency)}
          </span>
          <strong>общо {money(inv.total, inv.currency)}</strong>
        </div>
      )}
    </div>
  );
}
